import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "forja:scraping_monitor_enabled";
const TTL_MS = 5 * 60 * 1000; // 5 min

type CacheShape = { value: boolean; ts: number };

let memoryCache: CacheShape | null = null;
let inflight: Promise<boolean> | null = null;
const listeners = new Set<(v: boolean) => void>();

function readPersisted(): CacheShape | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheShape;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writePersisted(value: boolean) {
  try {
    const payload: CacheShape = { value, ts: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    memoryCache = payload;
  } catch {
    // ignore
  }
}

/**
 * Synchronous read for hot paths (e.g. logRequest).
 * Returns false if not yet loaded — safe default = no logging.
 */
export function getCachedScrapingEnabled(): boolean {
  if (memoryCache && Date.now() - memoryCache.ts <= TTL_MS) {
    return memoryCache.value;
  }
  const persisted = readPersisted();
  if (persisted) {
    memoryCache = persisted;
    return persisted.value;
  }
  return false;
}

async function loadFromServer(): Promise<boolean> {
  if (inflight) return inflight;
  inflight = (async () => {
    const { data } = await supabase
      .from("site_settings")
      .select("value")
      .eq("key", "scraping_monitor_enabled")
      .maybeSingle();
    const value = data?.value === "true";
    writePersisted(value);
    listeners.forEach((l) => l(value));
    return value;
  })();
  try {
    return await inflight;
  } finally {
    inflight = null;
  }
}

export function clearScrapingEnabledCache() {
  memoryCache = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * React hook — returns current enabled state, refreshing from server on mount
 * and reacting to realtime changes on site_settings.
 */
export function useScrapingEnabled() {
  const [enabled, setEnabled] = useState<boolean>(() => getCachedScrapingEnabled());
  const [loading, setLoading] = useState<boolean>(!memoryCache);

  useEffect(() => {
    let mounted = true;
    loadFromServer()
      .then((v) => {
        if (mounted) {
          setEnabled(v);
          setLoading(false);
        }
      })
      .catch(() => {
        if (mounted) setLoading(false);
      });

    const listener = (v: boolean) => {
      if (mounted) setEnabled(v);
    };
    listeners.add(listener);

    const channel = supabase
      .channel("site_settings_scraping_toggle")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "site_settings",
          filter: "key=eq.scraping_monitor_enabled",
        },
        (payload) => {
          const newValue = (payload.new as { value?: string })?.value === "true";
          writePersisted(newValue);
          listeners.forEach((l) => l(newValue));
        }
      )
      .subscribe();

    return () => {
      mounted = false;
      listeners.delete(listener);
      supabase.removeChannel(channel);
    };
  }, []);

  return { enabled, loading };
}

/**
 * Admin-only: toggle the flag in DB. Updates cache optimistically.
 */
export async function setScrapingEnabled(value: boolean): Promise<void> {
  const { error } = await supabase
    .from("site_settings")
    .update({ value: value ? "true" : "false", updated_at: new Date().toISOString() })
    .eq("key", "scraping_monitor_enabled");
  if (error) throw error;
  writePersisted(value);
  listeners.forEach((l) => l(value));
}
